/**
 * Hero image tiles: the one tile that carries a muted looping clip.
 *
 * The markup has no autoplay attribute; this starts the clip unless the
 * visitor prefers reduced motion, in which case the clip and its control are
 * removed and the poster photo stays. The tiles render twice (phone carousel
 * and desktop mosaic), so only the visible copy plays, and the copies swap
 * when the layout crosses the desktop breakpoint. The pause control keeps
 * WCAG 2.2.2 honest for anything that moves for more than five seconds.
 */

const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );
const desktop = window.matchMedia( '(min-width: 1024px)' );

function setToggleState( toggle, paused ) {
	toggle.setAttribute( 'aria-pressed', paused ? 'true' : 'false' );
	toggle.setAttribute(
		'aria-label',
		paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause
	);
}

function playVisible( tile ) {
	const video = tile.querySelector( 'video' );

	if ( ! video ) {
		return;
	}

	if ( ! tile.offsetParent ) {
		video.pause();
		return;
	}

	video.play().catch( () => {
		const toggle = tile.querySelector( '[data-tile-video-toggle]' );
		if ( toggle ) {
			setToggleState( toggle, true );
		}
	} );
}

function initTile( tile ) {
	const video = tile.querySelector( 'video' );

	if ( ! video ) {
		return;
	}

	const toggle = tile.querySelector( '[data-tile-video-toggle]' );
	const removeClip = () => {
		video.remove();
		if ( toggle ) {
			toggle.remove();
		}
	};

	if ( reducedMotion.matches ) {
		removeClip();
		return;
	}

	// A clip that cannot load leaves the photo in place. With <source>
	// children the failure fires on the source, not the video.
	video.addEventListener( 'error', removeClip, { once: true } );
	video.querySelectorAll( 'source' ).forEach( ( source ) => {
		source.addEventListener( 'error', removeClip, { once: true } );
	} );

	playVisible( tile );

	if ( ! toggle ) {
		return;
	}

	toggle.addEventListener( 'click', () => {
		if ( video.paused ) {
			video.play().catch( () => setToggleState( toggle, true ) );
		} else {
			video.pause();
		}
	} );

	video.addEventListener( 'pause', () => setToggleState( toggle, true ) );
	video.addEventListener( 'play', () => setToggleState( toggle, false ) );
}

export default function initTileVideos() {
	const tiles = document.querySelectorAll( '[data-tile-video]' );

	if ( ! tiles.length ) {
		return;
	}

	tiles.forEach( initTile );

	if ( ! reducedMotion.matches ) {
		desktop.addEventListener( 'change', () => tiles.forEach( playVisible ) );
	}

	// Reduced motion switched on after load: drop every clip, photos stay.
	reducedMotion.addEventListener( 'change', ( event ) => {
		if ( ! event.matches ) {
			return;
		}
		tiles.forEach( ( tile ) => {
			const video = tile.querySelector( 'video' );
			if ( video ) {
				video.pause();
				video.remove();
			}
			tile.querySelector( '[data-tile-video-toggle]' )?.remove();
		} );
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initTileVideos );
} else {
	initTileVideos();
}
